import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc, increment } from 'firebase/firestore';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

// Authentication functions
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Store/update subscriber info
    const userRef = doc(db, 'subscribers', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // New user
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        name: user.displayName,
        photoURL: user.photoURL,
        createdAt: new Date().toISOString(),
        lastActive: new Date().toISOString(),
        totalSpeeches: 0,
        totalItems: 0,
        totalApiCalls: 0
      });
    } else {
      // Existing user - update last active
      await updateDoc(userRef, {
        lastActive: new Date().toISOString()
      });
    }
    
    return user;
  } catch (error) {
    console.error('Sign in error:', error);
    throw error;
  }
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

// Variations cache functions
export const getVariationsFromCache = async (phrase) => {
  try {
    const varRef = doc(db, 'variations', phrase);
    const varSnap = await getDoc(varRef);
    
    if (varSnap.exists()) {
      return varSnap.data().variants;
    }
    return null;
  } catch (error) {
    console.error('Error getting variations:', error);
    return null;
  }
};

export const saveVariationsToCache = async (phrase, variants) => {
  try {
    const varRef = doc(db, 'variations', phrase);
    const varSnap = await getDoc(varRef);
    
    if (varSnap.exists()) {
      // Merge new variants with existing ones (remove duplicates)
      const existing = varSnap.data().variants || [];
      const merged = [...new Set([...existing, ...variants])];
      
      await updateDoc(varRef, {
        variants: merged,
        lastUpdated: new Date().toISOString(),
        usageCount: increment(1)
      });
    } else {
      // New entry
      await setDoc(varRef, {
        phrase: phrase,
        variants: variants,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        usageCount: 1
      });
    }
  } catch (error) {
    console.error('Error saving variations:', error);
  }
};

// Update user stats after speech
export const updateUserStats = async (userId, itemCount, duration) => {
  try {
    const userRef = doc(db, 'subscribers', userId);
    await updateDoc(userRef, {
      totalSpeeches: increment(1),
      totalItems: increment(itemCount),
      lastActive: new Date().toISOString()
    });
    
    // Log speech session
    const sessionRef = doc(db, 'sessions', `${userId}_${Date.now()}`);
    await setDoc(sessionRef, {
      userId: userId,
      itemCount: itemCount,
      duration: duration,
      timestamp: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating user stats:', error);
  }
};

// Track API call for cost analysis
export const trackApiCall = async (userId) => {
  try {
    const userRef = doc(db, 'subscribers', userId);
    await updateDoc(userRef, {
      totalApiCalls: increment(1),
      lastApiCall: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error tracking API call:', error);
  }
};
export { auth, db };